// Pure helpers for the zone boundary editor. The backend persists a list of
// per-boundary definitions (name, geoId, source, geometry) as JSONB and derives a
// unioned MULTIPOLYGON for spatial lookups — so per-boundary identity round-trips
// natively. The editor's session-scoped `BoundaryEntry` adds a client-only `id`
// (shared with the mapbox-gl-draw feature id) and a palette color on top of the
// wire `BoundaryDefinition`.

import { BOUNDARY_COLOR_PALETTE } from './boundaryMapConstants'

import type {
  BoundaryDefinitionApi,
  BoundaryGeometryApi,
  BoundarySourceApi,
  MultiPolygonGeometryApi,
  PolygonGeometryApi,
} from 'types/api/zones/zone'

// The wire shapes live in types/api/zones/zone.ts; aliased here so the editor's
// call sites read in boundary terms rather than transport terms.
export type PolygonGeometry = PolygonGeometryApi

export type MultiPolygonGeometry = MultiPolygonGeometryApi

export type BoundaryGeometry = BoundaryGeometryApi

export type BoundarySource = BoundarySourceApi

export type BoundaryDefinition = BoundaryDefinitionApi

export type BoundaryEntry = {
  /** Session-scoped id, shared with the mapbox-gl-draw feature id. */
  id: string
  name: string
  color: string
  source: BoundarySource
  geoId: string | null
  geometry: BoundaryGeometry
}

export type BoundaryFeature = {
  type: 'Feature'
  id: string
  geometry: BoundaryGeometry
  properties: { boundaryId: string; color: string; name: string }
}

export type LabelFeature = {
  type: 'Feature'
  geometry: { type: 'Point'; coordinates: [number, number] }
  properties: { name: string }
}

export type FeatureCollectionOf<F> = { type: 'FeatureCollection'; features: F[] }

const generateBoundaryId = () => `boundary-${Math.random().toString(36).slice(2, 11)}`

/** Top-level polygons of a geometry, each as Polygon coordinates. */
const geometryParts = (geometry: BoundaryGeometry): number[][][][] =>
  geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates

export const getBoundaryColor = (index: number) => BOUNDARY_COLOR_PALETTE[index % BOUNDARY_COLOR_PALETTE.length]

export const getFallbackBoundaryName = (index: number) => `Boundary ${index + 1}`

export const createBoundaryEntry = (
  geometry: BoundaryGeometry,
  name: string,
  index: number,
  source: BoundarySource,
  geoId: string | null = null
): BoundaryEntry => ({
  id: generateBoundaryId(),
  name,
  color: getBoundaryColor(index),
  source,
  geoId,
  geometry,
})

/** Editor entries -> wire definitions for PUT /geo-zones/{id}/boundaries. */
export const toBoundaryDefinitions = (boundaries: BoundaryEntry[]): BoundaryDefinition[] =>
  boundaries.map(({ name, geoId, source, geometry }) => ({ name, geoId, source, geometry }))

const isBoundaryGeometry = (value: unknown): value is BoundaryGeometry =>
  !!value &&
  typeof value === 'object' &&
  ((value as { type?: unknown }).type === 'Polygon' || (value as { type?: unknown }).type === 'MultiPolygon') &&
  Array.isArray((value as { coordinates?: unknown }).coordinates)

const isBoundarySource = (value: unknown): value is BoundarySource => value === 'TIGERWEB' || value === 'CUSTOM'

/** Wire definitions -> editor entries. Malformed entries fall back to positional names / CUSTOM. */
export const fromBoundaryDefinitions = (definitions: unknown): BoundaryEntry[] => {
  if (!Array.isArray(definitions)) return []
  return definitions.reduce<BoundaryEntry[]>((acc, raw, index) => {
    if (!raw || typeof raw !== 'object' || !isBoundaryGeometry((raw as { geometry?: unknown }).geometry)) return acc
    const record = raw as { name?: unknown; geoId?: unknown; source?: unknown; geometry: BoundaryGeometry }
    const name = typeof record.name === 'string' && record.name.trim() ? record.name : getFallbackBoundaryName(index)
    const geoId = typeof record.geoId === 'string' && record.geoId ? record.geoId : null
    const source: BoundarySource = isBoundarySource(record.source) ? record.source : 'CUSTOM'
    acc.push({
      id: generateBoundaryId(),
      name,
      color: getBoundaryColor(index),
      source,
      geoId,
      geometry: record.geometry,
    })
    return acc
  }, [])
}

/**
 * Coordinate-level concatenation of every entry's polygons into a single MultiPolygon
 * — for thumbnail and read-only overlay rendering only. This is NOT a topological
 * ST_Union (the backend does that server-side into geo_zone.boundary_shape); overlapping
 * polygons remain visible as separate parts. Accepts either editor entries or wire
 * definitions.
 */
export const unionForDisplay = (boundaries: readonly { geometry: BoundaryGeometry }[]): MultiPolygonGeometry | null => {
  if (boundaries.length === 0) return null
  return {
    type: 'MultiPolygon',
    coordinates: boundaries.flatMap(boundary => geometryParts(boundary.geometry)),
  }
}

const ringSignedArea = (ring: number[][]): number => {
  if (ring.length < 3) return 0
  const [firstX, firstY] = ring[0]
  const [lastX, lastY] = ring[ring.length - 1]
  const closedRing = firstX === lastX && firstY === lastY ? ring : [...ring, ring[0]]

  let doubledArea = 0
  for (let i = 0; i < closedRing.length - 1; i += 1) {
    const [x1, y1] = closedRing[i]
    const [x2, y2] = closedRing[i + 1]
    doubledArea += x1 * y2 - x2 * y1
  }
  return doubledArea / 2
}

/** Shoelace centroid of the outer ring; bbox center fallback for degenerate rings. */
export const getPolygonLabelPoint = (polygon: PolygonGeometry): [number, number] => {
  const ring = polygon.coordinates[0] ?? []
  if (ring.length === 0) return [0, 0]

  const [firstX, firstY] = ring[0]
  const [lastX, lastY] = ring[ring.length - 1]
  const closedRing = firstX === lastX && firstY === lastY ? ring : [...ring, ring[0]]

  let doubledArea = 0
  let centroidX = 0
  let centroidY = 0
  for (let i = 0; i < closedRing.length - 1; i += 1) {
    const [x1, y1] = closedRing[i]
    const [x2, y2] = closedRing[i + 1]
    const cross = x1 * y2 - x2 * y1
    doubledArea += cross
    centroidX += (x1 + x2) * cross
    centroidY += (y1 + y2) * cross
  }

  if (Math.abs(doubledArea) < 1e-12) {
    const xs = ring.map(([x]) => x)
    const ys = ring.map(([, y]) => y)
    return [(Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...ys) + Math.max(...ys)) / 2]
  }

  const signedArea = doubledArea / 2
  return [centroidX / (6 * signedArea), centroidY / (6 * signedArea)]
}

/** One label position per entry — the centroid of its largest part. */
export const getBoundaryLabelPoint = (geometry: BoundaryGeometry): [number, number] => {
  const parts = geometryParts(geometry)
  let largest = parts[0] ?? []
  let largestArea = -1
  parts.forEach(part => {
    const area = Math.abs(ringSignedArea(part[0] ?? []))
    if (area > largestArea) {
      largestArea = area
      largest = part
    }
  })
  return getPolygonLabelPoint({ type: 'Polygon', coordinates: largest })
}

export const buildBoundaryFeatureCollection = (boundaries: BoundaryEntry[]): FeatureCollectionOf<BoundaryFeature> => ({
  type: 'FeatureCollection',
  features: boundaries.map(boundary => ({
    type: 'Feature',
    id: boundary.id,
    geometry: boundary.geometry,
    properties: { boundaryId: boundary.id, color: boundary.color, name: boundary.name },
  })),
})

export const buildLabelFeatureCollection = (boundaries: BoundaryEntry[]): FeatureCollectionOf<LabelFeature> => ({
  type: 'FeatureCollection',
  features: boundaries.map(boundary => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: getBoundaryLabelPoint(boundary.geometry) },
    properties: { name: boundary.name },
  })),
})

// ── Static-map thumbnail overlay ─────────────────────────────────────────────
// The Mapbox Static Images API encodes the overlay GeoJSON in the request URL
// (~8KB limit), so a real boundary (a city can be a MultiPolygon with hundreds of
// parts and thousands of points) must be coarsened first. For a 120×90 thumbnail a
// rough outline is plenty: keep the largest parts' outer rings, downsample points,
// and round coordinates. Pure + testable; the URL/token assembly stays in the view.

const partsToGeometry = (parts: number[][][][]): BoundaryGeometry =>
  parts.length === 1 ? { type: 'Polygon', coordinates: parts[0] } : { type: 'MultiPolygon', coordinates: parts }

type SimplifyOptions = { maxParts?: number; maxPointsPerRing?: number; precision?: number }

const downsampleRing = (ring: number[][], maxPoints: number, round: (n: number) => number): number[][] => {
  const sampled =
    ring.length <= maxPoints
      ? ring
      : Array.from({ length: maxPoints }, (_, i) => ring[Math.min(ring.length - 1, Math.floor((i * ring.length) / maxPoints))])
  const out = sampled.map(([x, y]) => [round(x), round(y)])
  const [fx, fy] = out[0] ?? [0, 0]
  const [lx, ly] = out[out.length - 1] ?? [0, 0]
  if (out.length > 0 && (fx !== lx || fy !== ly)) out.push([fx, fy]) // keep the ring closed
  return out
}

/** Coarsen a boundary to a small point budget for the static-map thumbnail overlay. */
export const simplifyBoundaryForThumbnail = (geometry: BoundaryGeometry, options: SimplifyOptions = {}): BoundaryGeometry => {
  const { maxParts = 8, maxPointsPerRing = 28, precision = 3 } = options
  const factor = 10 ** precision
  const round = (n: number) => Math.round(n * factor) / factor

  const parts = [...geometryParts(geometry)]
    .sort((a, b) => Math.abs(ringSignedArea(b[0] ?? [])) - Math.abs(ringSignedArea(a[0] ?? [])))
    .slice(0, maxParts)
    .map(rings => [downsampleRing(rings[0] ?? [], maxPointsPerRing, round)]) // outer ring only — drop holes

  return partsToGeometry(parts)
}

export type ThumbnailOverlayStyle = { fill: string; fillOpacity: number; stroke: string; strokeWidth: number }

export type ThumbnailOverlayFeature = {
  type: 'Feature'
  properties: { fill: string; 'fill-opacity': number; stroke: string; 'stroke-width': number }
  geometry: BoundaryGeometry
}

/** Simplified boundary wrapped as a Mapbox simplestyle Feature for the static overlay. */
export const buildThumbnailOverlayFeature = (
  geometry: BoundaryGeometry,
  style: ThumbnailOverlayStyle,
  options?: SimplifyOptions
): ThumbnailOverlayFeature => ({
  type: 'Feature',
  properties: { fill: style.fill, 'fill-opacity': style.fillOpacity, stroke: style.stroke, 'stroke-width': style.strokeWidth },
  geometry: simplifyBoundaryForThumbnail(geometry, options),
})
